import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { ConfirmContext, type ConfirmFn, type ConfirmOptions } from './confirmContext';

/** Imperative confirmation dialog. Wrap the app once, then use `useConfirm()`. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [typed, setTyped] = useState('');
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setTyped('');
    setOptions(opts);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = useCallback((result: boolean) => {
    resolver.current?.(result);
    resolver.current = null;
    setOptions(null);
  }, []);

  const requireOk = !options?.requireText || typed === options.requireText;
  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog open={!!options} onClose={() => close(false)} maxWidth="xs" fullWidth>
        {options && (
          <>
            <DialogTitle>{options.title}</DialogTitle>
            <DialogContent>
              {options.description && (
                <DialogContentText component="div" sx={{ mb: options.requireText ? 2 : 0 }}>
                  {options.description}
                </DialogContentText>
              )}
              {options.requireText && (
                <TextField
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={options.requireText}
                  label={`Type "${options.requireText}" to confirm`}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && requireOk) close(true);
                  }}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => close(false)}>{options.cancelText ?? 'Cancel'}</Button>
              <Button
                variant="contained"
                color={options.severity === 'error' ? 'error' : 'primary'}
                disabled={!requireOk}
                onClick={() => close(true)}
              >
                {options.confirmText ?? 'Confirm'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
}
